import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CLIENT_QUICK_FILTERS } from '@repo/shared/marketing';
import type { ClientQuickFilter } from '../components/client-quick-filter-options';

/**
 * `?quickFilter=<id>` — a link from elsewhere (Reports › Clients "Text all 37", "Win them back") opens the list on one
 * Quick filter, where the usual select-all → Text flow takes over.
 */
export interface ClientQuickFilterSearch {
  quickFilter?: ClientQuickFilter;
}

export function validateClientQuickFilterSearch(search: Record<string, unknown>): ClientQuickFilterSearch {
  const { quickFilter } = search;
  return typeof quickFilter === 'string' && (CLIENT_QUICK_FILTERS as readonly string[]).includes(quickFilter)
    ? { quickFilter: quickFilter as ClientQuickFilter }
    : {};
}

/**
 * Hands a `?quickFilter=` from the URL to the page once (it becomes the ordinary, removable Quick filter pill), then
 * drops it from the URL so the Text view's own `?text=` navigation and a later refresh start from the plain list.
 * `orgRouteParam` is the URL's `$orgId` segment (the salon short code); `onApply` must be stable.
 */
export function useApplyClientQuickFilterSearch(
  orgRouteParam: string,
  quickFilter: ClientQuickFilter | undefined,
  onApply: (quickFilter: ClientQuickFilter) => void
) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!quickFilter) return;
    onApply(quickFilter);
    void navigate({
      to: '/$orgId/clients',
      params: { orgId: orgRouteParam },
      search: ({ quickFilter: _quickFilter, ...rest }: ClientQuickFilterSearch & Record<string, unknown>) => rest,
      replace: true,
    });
  }, [quickFilter, navigate, onApply, orgRouteParam]);
}
